import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EditClientModal from "./EditClientModal";
import AddClientModal from "./AddClientModal";
import { ColorRing } from "react-loader-spinner";
import { formatDate } from "../../ui/formatDate";
import { FaPlus } from "react-icons/fa";

const ClientsTable = () => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const userRole = localStorage.getItem("role");

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(buildApiUrl("/api/clients"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        setClients(data);
      } else {
        setError("Invalid response format. Expected an array.");
      }
    } catch (error) {
      setError("An error occurred while fetching clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredClients = clients.filter(
    (client) =>
      client.firmName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.mobileNo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredClients.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (clientId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(buildApiUrl(`/api/clients/${clientId}`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setClients(clients.filter((client) => client._id !== clientId));
        toast.success("Client deleted successfully!");
      } else {
        toast.error("Failed to delete client.");
      }
    } catch (error) {
      toast.error("An error occurred while deleting the client.");
    }
  };

  const handleAddClient = () => {
    setIsAddClientModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedClient(null);
  };

  const closeAddClientModal = () => {
    setIsAddClientModalOpen(false);
  };

  return (
    <div className="bg-white">
      <div className="">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between sm:space-x-4 w-full">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search Clients..."
              className="px-2 py-1 border border-gray-300 bg-gray-50 rounded-md shadow-sm w-full sm:w-1/3 focus:outline-none focus:ring-2 focus:ring-blue-700"
            />
            <button
              onClick={handleAddClient}
              className="bg-indigo-600 flex gap-1 justify-center items-center text-white px-3 py-2 text-xs font-medium w-full sm:w-fit rounded-md shadow-lg hover:bg-indigo-800 mt-2 sm:mt-0"
              style={{
                display:
                  userRole === "sales executive" ? "none" : "inline-flex",
              }}
            >
              <FaPlus size={12} />
              Add Client
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center">
            <ColorRing
              visible={true}
              height="80"
              width="80"
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
            />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500">
            <p>{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-w-[100vw] rounded-md border shadow-md border-gray-300 max-h-[40rem]">
            <table className="min-w-full table-auto rounded-lg overflow-scroll shadow-md border-spacing-0 border-collapse">
              <thead className="bg-gray-700 text-white sticky top-0 z-10">
                <tr>
                  <th className="p-2 text-left font-normal tracking-wide">#</th>
                  <th className="p-2 text-left font-normal tracking-wide min-w-40">
                    Created At
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Firm Name
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Contact Person
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Email
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Phone
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Sales Executive
                  </th>
                  {userRole !== "sales executive" && (
                    <>
                      <th className="p-2 text-left font-normal tracking-wide">
                        Username
                      </th>
                      <th className="p-2 text-left font-normal tracking-wide">
                        Password
                      </th>
                      <th className="p-2 text-left font-normal tracking-wide min-w-20">
                        Actions
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white">
                {currentItems
                  .filter((client) => !client.isDeleted)
                  .map((client, index) => (
                    <tr
                      key={client._id}
                      className="hover:bg-gray-100 border divide-x text-gray-700 divide-gray-200 border-b border-collapse border-gray-200 transition-colors duration-200 cursor-pointer"
                    >
                      <td className="px-2 py-0 5">
                        {indexOfFirstItem + index + 1}
                      </td>
                      <td className="px-2 py-0.5">
                        {formatDate(client.createdAt)}
                      </td>
                      <td className="px-2 py-0.5 font-semibold">
                        {client.firmName}
                      </td>
                      <td className="px-2 py-0.5">{client.contactPerson}</td>
                      <td className="px-2 py-0.5">{client.email}</td>
                      <td className="px-2 py-0.5">{client.mobileNo}</td>
                      <td className="px-2 py-0.5">
                        {client?.assignedSalesExecutive?.name}
                      </td>
                      {userRole !== "sales executive" && (
                        <>
                          <td className="px-2 py-0.5">{client.username}</td>
                          <td className="px-2 py-0.5">{client.password}</td>
                          <td className="px-2 py-0.5 text-sm text-center space-x-2">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleEdit(client)}
                                className="text-blue-500 hover:text-blue-700"
                              >
                                <HiOutlinePencil size={16} />
                              </button>
                              <button
                                onClick={() => handleDelete(client._id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <HiOutlineTrash size={16} />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, clients.length)} of {clients.length}{" "}
            clients
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 text-gray-600 rounded-lg"
            >
              Prev
            </button>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 text-gray-600 rounded-lg"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ToastContainer />
      {isEditModalOpen && selectedClient && (
        <EditClientModal
          client={selectedClient}
          onClose={closeEditModal}
          onSubmit={fetchClients}
        />
      )}
      {isAddClientModalOpen && (
        <AddClientModal onClose={closeAddClientModal} onSubmit={fetchClients} />
      )}
    </div>
  );
};

export default React.memo(ClientsTable);
